import { collection, query, where, orderBy, onSnapshot, Timestamp, addDoc, getDocs, limit, doc, updateDoc, deleteDoc, getDoc, writeBatch } from 'firebase/firestore';

import { db } from '../config';

import type { Group, LastPost } from '@/types/support';
import { isGroupEditor, assertMembershipAllowed } from './group-members';
import { getOrganizationManagers } from './organizations/managers';
import { isOrganizationManager } from './organizations/isOrganizationMember';
import { isGroupVisibleInOrg } from '@/lib/groups/groupKind';


// Cache configuration
const GROUPS_CACHE_KEY = 'supportGroups';
const CACHE_EXPIRY = 1000 * 60 * 30; // 30 minutes

async function getCachedGroups(): Promise<Group[]> {
  try {
    const cache = localStorage.getItem(GROUPS_CACHE_KEY);
    if (!cache) return [];
    
    const { groups, timestamp } = JSON.parse(cache);
    const isExpired = Date.now() - timestamp > CACHE_EXPIRY;
    
    return isExpired ? [] : groups;
  } catch {
    return [];
  }
}

async function cacheGroups(groups: Group[]) {
  try {
    localStorage.setItem(GROUPS_CACHE_KEY, JSON.stringify({
      groups,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error caching groups:', error);
  }
}

export function clearGroupsCache() {
  try {
    localStorage.removeItem(GROUPS_CACHE_KEY);
    console.log('✅ Groups cache cleared');
  } catch (error) {
    console.error('Error clearing groups cache:', error);
  }
}

export async function getGroupUnreadCount(groupId: string): Promise<number> {
  try {
    const messagesQuery = query(
      collection(db, 'groupMessages'),
      where('groupId', '==', groupId),
      where('createdAt', '>', Timestamp.now())
    );
    
    const messagesSnapshot = await getDocs(messagesQuery);
    return messagesSnapshot.size;
  } catch (error) {
    console.error('Error:', error);
    return 0;
  }
}

export async function getLastPost(groupId: string): Promise<LastPost | null> {
  try {
    const postsQuery = query(
      collection(db, 'groupPosts'),
      where('groupId', '==', groupId),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    
    const snapshot = await getDocs(postsQuery);
    if (snapshot.empty) return null;
    
    const post = snapshot.docs[0].data();
    return {
      title: post.title,
      content: post.content,
      userName: post.userName,
      createdAt: post.createdAt.toDate()
    };
  } catch (error) {
    console.error('Error:', error);
    return null;
  }
}

export async function updateGroupLastPost(groupId: string) {
  try {
    const lastPost = await getLastPost(groupId);
    await updateDoc(doc(db, 'supportGroups', groupId), {
      lastPost: lastPost ? {
        title: lastPost.title,
        content: lastPost.content,
        userName: lastPost.userName,
        createdAt: Timestamp.fromDate(lastPost.createdAt)
      } : null,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error:', error);
  }
}

export interface GroupUpdatesOptions {
  /**
   * The member's groups list scope (mobile parity): the organization's groups
   * PLUS personal (org-less) groups, other organizations' groups hidden
   * (isGroupVisibleInOrg). Off by default — manager screens want the
   * organization's groups only.
   */
  includePersonal?: boolean;
}

export function subscribeToGroupUpdates(
  onUpdate: (groups: Group[]) => void,
  onError?: (error: Error) => void,
  organizationId?: string,
  options: GroupUpdatesOptions = {}
) {
  const includePersonal = options.includePersonal === true;
  const inScope = (group: { organization?: string | null }): boolean =>
    includePersonal
      ? isGroupVisibleInOrg(group, organizationId)
      : !organizationId || group.organization === organizationId;

  // First try to get cached data
  getCachedGroups().then(groups => {
    if (groups.length > 0) {
      onUpdate(groups.filter(inScope));
    }
  });

  // Set up real-time listener. Personal groups have no organization field, so
  // the member's scope reads all groups and filters, as mobile does.
  const groupsQuery = organizationId && !includePersonal
    ? query(
        collection(db, 'supportGroups'),
        where('organization', '==', organizationId),
        orderBy('createdAt', 'desc')
      )
    : query(
        collection(db, 'supportGroups'),
        orderBy('createdAt', 'desc')
      );

  return onSnapshot(
    groupsQuery,
    {
      next: async (snapshot) => {
        try {
          const groupsWithDetails = snapshot.docs.filter((d) => inScope(d.data())).map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              lastPost: data.lastPost ? {
                ...data.lastPost,
                createdAt: data.lastPost.createdAt.toDate()
              } : null,
              createdAt: data.createdAt?.toDate() || new Date()
            } as Group;
          });

          // Show the list now; a group without a stored lastPost used to hold
          // the whole list until its own posts query returned (one per group).
          onUpdate(groupsWithDetails);

          const missing = groupsWithDetails.filter((g) => !g.lastPost);
          if (missing.length > 0) {
            const found = await Promise.all(missing.map((g) => getLastPost(g.id)));
            if (found.some(Boolean)) {
              const byId = new Map(missing.map((g, i) => [g.id, found[i]]));
              const filled = groupsWithDetails.map((g) => (byId.get(g.id) ? { ...g, lastPost: byId.get(g.id)! } : g));
              onUpdate(filled);
              await cacheGroups(filled);
              return;
            }
          }
          await cacheGroups(groupsWithDetails);
        } catch (error) {
          console.error('Error processing groups:', error);
          if (onError) onError(error as Error);
        }
      },
      error: async (error) => {
        console.error('Error in groups subscription:', error);
        console.error('Error:', error);
        
        // Try to serve cached data on error
        const cachedGroups = await getCachedGroups();
        if (cachedGroups.length > 0) {
          onUpdate(cachedGroups.filter(inScope));
        }
        
        if (onError) onError(error);
      }
    }
  );
}

export async function createGroup(data: {
  name: string;
  description: string;
  categories: string[];
  isPrivate: boolean;
  creatorId: string;
  creatorName: string;
  organization?: string; // Optional organization ID
  parentGroupId?: string; // Optional parent group ID for hierarchy
  groupType: 'parent' | 'subgroup'; // Fixed type determined at creation
}): Promise<Group | null> {
  try {
    console.log('🔄 Creating group with organization:', data.organization);
    

    
    // ❌ DISABLED: No longer auto-adding organization managers to new groups

    // ✅ Pure group data - no denormalized membership counts
    const groupData: any = {
      name: data.name,
      description: data.description,
      categories: data.categories,
      isPrivate: data.isPrivate,
      creatorId: data.creatorId,
      creatorName: data.creatorName,
      // ❌ REMOVED: members count - now calculated virtually
      lastPost: null,
      groupType: data.groupType, // Add the groupType field
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // Only add optional fields if they have values
    if (data.organization) {
      groupData.organization = data.organization;
    }
    if (data.parentGroupId) {
      groupData.parentGroupId = data.parentGroupId;
    }

    const docRef = await addDoc(collection(db, 'supportGroups'), groupData);

    console.log('✅ Group created with ID:', docRef.id);

    // ✅ Auto-add creator to groupMembers with "editor" role
    // Creators should be editors so they can manage their groups
    // Guarded: sections aren't joinable (so the creator of a section is NOT
    // auto-added), and if the creator already has a different subgroup in
    // this org, skip rather than create a second membership — creating the
    // group itself must still succeed either way.
    try {
      await assertMembershipAllowed(docRef.id, data.creatorId);
      await addDoc(collection(db, 'groupMembers'), {
        groupId: docRef.id,
        userId: data.creatorId,
        role: 'editor', // Creator is an editor so they can edit/delete their group
        joinedAt: Timestamp.now(),
        addedBy: data.creatorId
      });
      console.log('✅ Creator added to groupMembers as editor');
      
      // Verify the membership is readable (poll until it exists)
      // This ensures it's visible when GroupList checks membership
      let membershipVisible = false;
      let retries = 0;
      const maxRetries = 10;
      
      while (!membershipVisible && retries < maxRetries) {
        const membershipQuery = query(
          collection(db, 'groupMembers'),
          where('groupId', '==', docRef.id),
          where('userId', '==', data.creatorId)
        );
        const membershipCheck = await getDocs(membershipQuery);
        
        if (!membershipCheck.empty) {
          membershipVisible = true;
          console.log('✅ Membership verified as readable');
        } else {
          retries++;
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      if (!membershipVisible) {
        console.warn('⚠️ Membership not immediately visible, but it should appear shortly');
      }
    } catch (memberError) {
      console.error('⚠️ Error adding creator to groupMembers:', memberError);
      // Don't fail group creation if member addition fails
      // The user can manually join later if needed
    }

    // Return the created group data for optimistic update
    const createdGroup: Group = {
      id: docRef.id,
      name: groupData.name,
      description: groupData.description,
      categories: groupData.categories,
      isPrivate: groupData.isPrivate,
      lastPost: null,
      groupType: groupData.groupType,
      creatorId: groupData.creatorId,
      creatorName: groupData.creatorName,
      organization: groupData.organization,
      parentGroupId: groupData.parentGroupId,
      createdAt: groupData.createdAt.toDate(),
      updatedAt: groupData.updatedAt.toDate()
    };

    return createdGroup;
  } catch (error) {
    console.error('❌ Error creating group:', error);
    return null;
  }
}

export async function updateGroup(groupId: string, data: {
  name: string;
  description: string;
  categories: string[];
  isPrivate: boolean;
  updatedAt: Date;
}): Promise<boolean> {
  try {
    await updateDoc(doc(db, 'supportGroups', groupId), {
      ...data,
      updatedAt: Timestamp.fromDate(data.updatedAt)
    });
    return true;
  } catch (error) {
    console.error('Error:', error);
    return false;
  }
}

// Collections keyed by `groupId` that deleteGroup removes with the group.
const GROUP_CHILD_COLLECTIONS = ['groupMembers', 'groupMessages', 'groupPosts', 'groupReads'] as const;
const DELETE_BATCH_SIZE = 450;

export async function deleteGroup(groupId: string, userId: string): Promise<boolean> {
  try {
    // Get group information first to check for organization
    const groupRef = doc(db, 'supportGroups', groupId);
    const groupDoc = await getDoc(groupRef);
    
    if (!groupDoc.exists()) {
      throw new Error('Group not found');
    }
    
    const groupData = groupDoc.data();
    
    // Check if user is an editor
    const isEditor = await isGroupEditor(groupId, userId);
    
    // If not an editor, check if they're an organization manager
    let hasPermission = isEditor;
    if (!isEditor) {
      if (groupData.organization) {
        // Check if user is a manager of this organization
        hasPermission = await isOrganizationManager(groupData.organization, userId);
      }
    }
    
    if (!hasPermission) {
      throw new Error('Only editors or organization managers can delete groups');
    }

    // Owner decision #4: a section is deleted only once it is empty — its
    // subgroups are moved to another section or out of any section first.
    if (groupData.groupType === 'parent') {
      const subgroups = await getDocs(
        query(collection(db, 'supportGroups'), where('parentGroupId', '==', groupId), limit(1))
      );
      if (!subgroups.empty) {
        throw new Error('Section still has subgroups');
      }
    }

    // Children FIRST, the group doc LAST: if any step fails the group is
    // still there and the delete can simply be retried, instead of leaving a
    // deleted group with orphaned members/messages. A writeBatch holds at
    // most 500 writes, so each collection is committed in chunks.
    for (const collectionName of GROUP_CHILD_COLLECTIONS) {
      const snapshot = await getDocs(
        query(collection(db, collectionName), where('groupId', '==', groupId))
      );
      for (let i = 0; i < snapshot.docs.length; i += DELETE_BATCH_SIZE) {
        const batch = writeBatch(db);
        snapshot.docs.slice(i, i + DELETE_BATCH_SIZE).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
    }

    await deleteDoc(groupRef);

    return true;
  } catch (error) {
    console.error('Error:', error);
    return false;
  }
}

export async function getGroupLeaderboard(groupId: string) {
  try {
    const groupRef = doc(db, 'groups', groupId);
    const membersRef = collection(groupRef, 'members');
    const membersSnapshot = await getDocs(membersRef);
    
    const leaderboardData = await Promise.all(
      membersSnapshot.docs.map(async (memberDoc) => {
        const memberData = memberDoc.data();
        const userRef = doc(db, 'users', memberDoc.id);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();

        return {
          userId: memberDoc.id,
          userName: userData ? `${userData.firstName} ${userData.lastName}` : 'Unknown User',
          points: memberData.points || 0,
          achievements: memberData.achievements || [],
          rank: 0 // Will be calculated after sorting
        };
      })
    );

    // Sort by points and assign ranks
    const sortedData = leaderboardData
      .sort((a, b) => b.points - a.points)
      .map((entry, index) => ({
        ...entry,
        rank: index + 1
      }));

    return sortedData;
  } catch (error) {
    console.error('Error fetching group leaderboard:', error);
    throw error;
  }
}

// Note: Member management functions have been moved to group-members.ts
// This consolidates the member management system and provides better
// permission handling and role management capabilities.
// 
// For member operations, import from '@/lib/firebase/queries/group-members':
// - getGroupMembers(groupId): Returns members with role information
// - addGroupMember(groupId, userId, addedByUserId): With permission checks
// - removeGroupMember(groupId, userId, removedByUserId): With permission checks

/**
 * Utility function to add all organization managers to all organization groups
 * This can be used to retroactively sync existing managers with existing groups
 */
export async function syncOrganizationManagersToGroups(organizationId: string): Promise<boolean> {
  // ❌ DISABLED: Auto-sync removed to prevent automatic groupMembers recovery
  console.log('🚫 Organization manager auto-sync is disabled');
  return true;
}